//================================================================
//  Page: Total Contract Value 
//================================================================

//  Purpose: Contains table to add, edit and calculate contract values in a request

//  Properties:
//    - task = { useState, stores the currently selected request}
//    - taskSection = { useReducer, table shows only when its container is expanded }

//  Example:
//  
//    <TotalContractValue
//      taskSection={taskSection}
//      task={task}
//    ></TotalContractValue>

//================================================================

// Libraries
import React, { useState } from 'react';

// Contexts

// Components
import KebabDropdown from '../../../../../Components/KebabDropdown/KebabDropdown';

// Functions
import AddEditCostPane from './SidePanes/AddEditCostPane';
import WriteDocument from '../../../../../Library/WriteDocument';

// Images
import AddIcon from '../../../../../Components/Images/Icon_Add_Teal.svg';
import AddIconDisabled from '../../../../../Components/Images/Icon_Add_Grey.svg';

//CSS


export default function TotalContractValue({
    taskSection,
    task,
}) {

    //------------------------------------------------------
    //  useStates
    //------------------------------------------------------

    // For Add or Edit Cost Panes (bool)
    const [costPane, setCostPane] = useState('');

    // Stores data for Edit Cost Pane
    const [costData, setCostData] = useState({});

    //------------------------------------------------------
    //  Functions
    //------------------------------------------------------

    // Calculate total cost
    const getTotalContractValue = () => {

        let value = 0;

        task?.costs?.forEach((cost) => {
            // One-time cost
            if (cost.costtype === 'One-time')
                value += cost?.costperterm
            // Monthly or Yearly cost
            else
                value += cost?.costperterm * cost?.terms
        });

        return value;
    };

    // Delete line item
    const deleteCost = (del) => {

        // Create a new array of costs without the removed cost
        const newCostList = []

        task?.costs.forEach((cost) => {
            if (cost.costid !== del.costid) newCostList.push(cost)
        })

        return WriteDocument('requests', task?.requestid, { 'costs': newCostList }, true)

    }

    //------------------------------------------------------
    //  HTML
    //------------------------------------------------------

    return (
        <div className='p-[3%]' hidden={taskSection?.totalContractValueCollapsed}>

            {/* ===================================== */}
            {/*  Table                                */}
            {/* ===================================== */}

            <div className='w-full overflow-x-auto overflow-y-auto border border-[#D8D8D8] rounded px-[1%]'>
                <table className='w-full max-h-96 table-auto'>
                    <thead>
                        <tr className='text-[#424242] border-b border-b-[#D8D8D8]'>
                            <th className='py-[15px] px-4 font-medium'>Description</th>
                            <th className='py-[15px] px-4 font-medium'>Cost per Term (exTax)</th>
                            <th className='py-[15px] px-4 font-medium'>Terms</th>
                            <th className='py-[15px] px-4 font-medium'>Term Type</th>
                            <th className='py-[15px] px-4 font-medium'>Total (exTax)</th>
                            <th className='py-[15px] px-4 font-medium'>Currency</th>
                            <th className='py-[15px] px-4'>
                                <div className='flex flex-row items-center justify-center w-6'>
                                    <img
                                        src={task?.sentfinalapprovals || task?.status === 'Completed' || task?.status === 'Rejected' || task?.status === 'Cancelled' ? AddIconDisabled : AddIcon}
                                        alt='Add'
                                        className={!task?.sentfinalapprovals ? 'cursor-pointer !w-10 !h-10' : '!w-10 !h-10'}
                                        onClick={() => !task?.sentfinalapprovals && setCostPane('add')}
                                    >
                                    </img>
                                </div>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {
                            // No Costs found
                            task?.costs?.length === 0 || task?.costs === undefined ?
                                (
                                    <tr>
                                        <td colSpan={6}>
                                            <div className='text-center p-[2%]'>
                                                Click on the '+' to add costs
                                            </div>
                                        </td>
                                    </tr>
                                )
                                :
                                (
                                    // Map in each Cost
                                    task?.costs?.map((cost) => (
                                        <tr key={cost?.costid} className='border-b border-b-[#D8D8D8]'>
                                            <td className='py-[15px] px-4'>{cost?.description}</td>
                                            <td className='py-[15px] px-4'>{isNaN(cost?.costperterm) ? cost?.onetimecost.toLocaleString() : cost?.costperterm.toLocaleString()}</td>
                                            <td className='py-[15px] px-4'>{cost?.terms < 1 ? '--' : cost?.terms}</td>
                                            <td className='py-[15px] px-4'>{cost?.costtype}</td>
                                            <td className='py-[15px] px-4'>{(cost?.costtype === 'Monthly' || cost?.costtype === 'Yearly') ? (cost?.costperterm * cost?.terms).toLocaleString() : cost?.costperterm.toLocaleString()}</td>
                                            <td className='py-[15px] px-4'>AUD</td>
                                            {
                                                task?.sentfinalapprovals || task?.status === 'Completed' || task?.status === 'Rejected' || task?.status === 'Cancelled' ?
                                                    <td className='py-[15px] px-4'></td>
                                                    :
                                                    <td className='py-[15px] px-4'>
                                                        <KebabDropdown children={
                                                            <div className=' bg-white rounded shadow-lg z-10'>
                                                                <button
                                                                    onClick={() => { setCostPane('edit'); setCostData(cost); }}
                                                                    className={`block w-full text-left px-4 py-2 ${task?.sentfinalapprovals ? 'text-gray-200' : 'text-gray-700 hover:bg-gray-100'}`}
                                                                    disabled={task?.sentfinalapprovals ? true : false}
                                                                >
                                                                    Edit
                                                                </button>
                                                                <button
                                                                    onClick={() => deleteCost(cost)}
                                                                    className={`block w-full text-left px-4 py-2 ${task?.sentfinalapprovals ? 'text-red-200' : 'text-red-600 hover:bg-gray-100'}`}
                                                                    disabled={task?.sentfinalapprovals ? true : false}
                                                                >
                                                                    Remove
                                                                </button>
                                                            </div>
                                                        }></KebabDropdown>
                                                    </td>
                                            }

                                        </tr>
                                    ))
                                )
                        }

                        {/* Total Contract Value */}
                        <tr className='py-[15px] px-[5px]' hidden={task?.costs?.length < 1}>
                            <td className='py-[15px] px-4 text-right' colSpan={7}>
                                <div className='flex flex-row gap-2 items-center justify-end'>
                                    <p className='mb-0'>Total Contract Value (exTax):</p>
                                    <p className='font-semibold mb-0'>{getTotalContractValue().toLocaleString()} AUD</p>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            {/* ===================================== */}
            {/*  Sidepanes                            */}
            {/* ===================================== */}

            <AddEditCostPane
                paneOpen={costPane}
                setPaneOpen={setCostPane}
                task={task}
                costData={costData}
            ></AddEditCostPane>

        </div>

    )
}